import { router, useForm, Link as InertiaLink } from "@inertiajs/react";
import { useState } from "react";
import { AlertTriangle, CheckCircle, Copy, FlaskConical, Link, Pause, Play, Rocket, Sparkles, Unlink, Zap } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import BillingSection, { BillingStatus } from "./BillingSection";

interface GoogleStatus {
  connected: boolean;
  revoked: boolean;
  connected_email: string | null;
  gmail_connected: boolean;
  calendar_connected: boolean;
}

interface ExchangeStatus {
  configured: boolean;
  api_configured: boolean;
  crm_url: string;
  username: string;
  automations_paused: boolean;
  connection_status: "connected" | "stale";
}

interface LeopardStatus {
  configured: boolean;
  username: string;
}

interface AutomationToggles {
  skm_auto_draft: boolean;
  resume_auto_optimize: boolean;
  submission_auto_generate: boolean;
  email_sequence_auto_send: boolean;
  job_alert_auto_match: boolean;
}

interface McpConnection {
  configured: boolean;
  client_id: string | null;
  client_secret: string | null;
  mcp_url: string | null;
}

interface EmailSafetyStatus {
  kill_switch_on: boolean;
  kill_switch_env: boolean;
  test_mode_on: boolean;
  test_mode_env: boolean;
  recipient_candidate: string;
  recipient_firm: string;
  recipient_system: string;
  env_recipient_candidate: string | null;
  env_recipient_firm: string | null;
  env_recipient_system: string | null;
}

interface Props {
  google: GoogleStatus;
  exchange: ExchangeStatus;
  leopard: LeopardStatus;
  ai_assist: boolean;
  go_mode: boolean;
  go_mode_unlocked: boolean;
  automations: AutomationToggles;
  blocklisted_practice_areas: string[];
  email_campaign_dedup_window_days: number;
  email_campaign_placed_dedup_window_days: number;
  billing: BillingStatus;
  mcp: McpConnection;
  email_safety: EmailSafetyStatus;
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</h2>
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1.5 font-mono text-gray-700 truncate">{value}</code>
        <button onClick={copy} className="shrink-0 text-gray-400 hover:text-gray-700 transition-colors">
          <Copy size={13} />
        </button>
        {copied && <span className="text-xs text-green-600">Copied!</span>}
      </div>
    </div>
  );
}

export default function SettingsIndex({ google, exchange, leopard, ai_assist, go_mode, go_mode_unlocked, automations, blocklisted_practice_areas, email_campaign_dedup_window_days, email_campaign_placed_dedup_window_days, billing, mcp, email_safety }: Props) {
  const [dedupWindow, setDedupWindow]               = useState(email_campaign_dedup_window_days);
  const [dedupWindowSaving, setDedupWindowSaving]   = useState(false);
  const [placedDedupWindow, setPlacedDedupWindow]   = useState(email_campaign_placed_dedup_window_days);
  const [placedDedupSaving, setPlacedDedupSaving]   = useState(false);

  function saveDedupWindow() {
    setDedupWindowSaving(true);
    router.patch("/settings/dedup-window",
      { email_campaign_dedup_window_days: dedupWindow },
      { onFinish: () => setDedupWindowSaving(false) }
    );
  }

  function savePlacedDedupWindow() {
    setPlacedDedupSaving(true);
    router.patch("/settings/dedup-window",
      { email_campaign_placed_dedup_window_days: placedDedupWindow },
      { onFinish: () => setPlacedDedupSaving(false) }
    );
  }

  const [blocklistChips, setBlocklistChips]   = useState<string[]>([...blocklisted_practice_areas].sort());
  const [blocklistInput, setBlocklistInput]   = useState("");
  const [blocklistSaving, setBlocklistSaving] = useState(false);

  function addBlocklistChip(raw: string) {
    const val = raw.trim();
    if (!val) return;
    if (blocklistChips.some((c) => c.toLowerCase() === val.toLowerCase())) return;
    setBlocklistChips((prev) => [...prev, val].sort());
    setBlocklistInput("");
  }

  function removeBlocklistChip(chip: string) {
    setBlocklistChips((prev) => prev.filter((c) => c !== chip));
  }

  function saveBlocklist() {
    setBlocklistSaving(true);
    router.post("/settings/blocklisted-practice-areas",
      { blocklisted_practice_areas: blocklistChips },
      { onFinish: () => setBlocklistSaving(false) }
    );
  }

  function handleBlocklistKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addBlocklistChip(blocklistInput);
    }
  }

  const recipientsForm = useForm({
    candidate: email_safety.recipient_candidate,
    firm:      email_safety.recipient_firm,
    system:    email_safety.recipient_system,
  });

  const submitRecipients = (e: React.FormEvent) => {
    e.preventDefault();
    recipientsForm.patch("/settings/email-safety/test-recipients");
  };

  const handleKillSwitch = () => {
    if (email_safety.kill_switch_on && !window.confirm("Re-enable email sending? Mail will go out to real recipients.")) return;
    router.post("/settings/email-safety/kill-switch/toggle");
  };

  // crm_url + username are configured per-deploy (Rails credentials / ENV) and
  // shown read-only in the form — not submitted. Controller strips them too.
  const exchangeForm = useForm({
    password:       "",
    session_id:     "",
    remember_token: "",
  });

  const leopardForm = useForm({
    username: leopard.username,
    password: "",
  });

  const handleDisconnectGoogle = () => router.delete("/settings/google/disconnect");
  const handleToggleAutomations = () => router.post("/settings/exchange/toggle");

  const handleSignOutOtherSessions = () => {
    if (!window.confirm("Sign out of every other session? Any other device currently logged in will be signed out immediately.")) return;
    router.delete("/sessions/others");
  };

  const submitExchange = (e: React.FormEvent) => {
    e.preventDefault();
    exchangeForm.post("/settings/exchange");
  };

  const submitLeopard = (e: React.FormEvent) => {
    e.preventDefault();
    leopardForm.post("/settings/leopard");
  };

  return (
    <AppLayout title="Settings">
      <div className="max-w-2xl">
        <h1 className="text-xl font-semibold text-gray-900 mb-6">Settings</h1>

        {/* Security */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Security" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Sessions</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Sign out of every other session. This device stays signed in.
              </div>
            </div>
            <button
              onClick={handleSignOutOtherSessions}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 border border-gray-200 rounded-md px-3 py-1.5 hover:border-red-200 transition-colors shrink-0"
            >
              Sign Out Other Sessions
            </button>
          </div>
        </section>

        {/* Email Safety */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Email Safety" />

          {/* Kill switch */}
          <div className="px-5 py-4 flex items-start justify-between gap-4 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-md border flex-shrink-0 ${email_safety.kill_switch_on ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                <AlertTriangle size={16} className={email_safety.kill_switch_on ? "text-red-600" : "text-gray-400"} />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Global Email Kill Switch</div>
                <div className="text-xs mt-0.5">
                  {email_safety.kill_switch_env
                    ? <span className="text-red-700 font-medium">Forced OFF by EMAIL_SEND_DISABLED env — no toggle available</span>
                    : email_safety.kill_switch_on
                      ? <span className="text-red-700">All outbound email is disabled. No mail will leave the system.</span>
                      : <span className="text-gray-500">Email sending is active. Toggle to block all outbound mail immediately.</span>
                  }
                </div>
              </div>
            </div>
            {!email_safety.kill_switch_env && (
              <button
                onClick={handleKillSwitch}
                className={`flex items-center gap-1.5 text-xs font-medium border rounded-md px-3 py-1.5 transition-colors flex-shrink-0 ${
                  email_safety.kill_switch_on
                    ? "text-green-700 border-green-200 hover:bg-green-50"
                    : "text-red-600 border-red-200 hover:bg-red-50"
                }`}
              >
                {email_safety.kill_switch_on ? <><Play size={12} /> Re-enable</> : <><AlertTriangle size={12} /> Disable All Email</>}
              </button>
            )}
          </div>

          {/* Test mode */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-md border flex-shrink-0 ${email_safety.test_mode_on ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}>
                  <FlaskConical size={16} className={email_safety.test_mode_on ? "text-amber-600" : "text-gray-400"} />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Test Mode</div>
                  <div className="text-xs mt-0.5">
                    {email_safety.test_mode_env
                      ? <span className="text-amber-700 font-medium">Forced ON by EMAIL_TEST_MODE env</span>
                      : email_safety.test_mode_on
                        ? <span className="text-amber-700">Active — all mail redirected to test recipients below</span>
                        : <span className="text-gray-500">Safe mode for testing. Rewrites all recipients to your test addresses.</span>
                    }
                  </div>
                </div>
              </div>
              {!email_safety.test_mode_env && (
                <button
                  onClick={() => router.post("/settings/email-safety/test-mode/toggle")}
                  className={`flex items-center gap-1.5 text-xs font-medium border rounded-md px-3 py-1.5 transition-colors flex-shrink-0 ${
                    email_safety.test_mode_on
                      ? "text-amber-700 border-amber-200 hover:bg-amber-50"
                      : "text-green-700 border-green-200 hover:bg-green-50"
                  }`}
                >
                  {email_safety.test_mode_on ? <><Pause size={12} /> Disable</> : <><FlaskConical size={12} /> Enable</>}
                </button>
              )}
            </div>

            <form onSubmit={submitRecipients} className="space-y-3">
              <p className="text-xs font-medium text-gray-600">Test Recipients</p>
              {(["candidate", "firm", "system"] as const).map((cat) => {
                const envVal = email_safety[`env_recipient_${cat}` as keyof EmailSafetyStatus] as string | null;
                return (
                  <div key={cat}>
                    <label className="block text-xs text-gray-500 mb-1 capitalize">{cat}</label>
                    {envVal ? (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1.5 font-mono text-amber-700 truncate">{envVal}</code>
                        <span className="text-xs text-amber-600 shrink-0">env override</span>
                      </div>
                    ) : (
                      <input
                        type="email"
                        value={recipientsForm.data[cat]}
                        onChange={e => recipientsForm.setData(cat, e.target.value)}
                        placeholder={`you+${cat}.docket@gmail.com`}
                        className={`w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 ${
                          email_safety.test_mode_on && !recipientsForm.data[cat]
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={recipientsForm.processing}
                  className="text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-md px-4 py-2 transition-colors"
                >
                  Save Recipients
                </button>
              </div>
            </form>
          </div>

          {/* Audit log link */}
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
            <InertiaLink
              href="/settings/email-safety/log"
              className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
            >
              View audit log →
            </InertiaLink>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Integrations" />

          {/* Google Sheets */}
          <div className="px-5 py-4 flex items-center justify-between gap-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 border border-gray-200">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                  <path d="M6 2h9l5 5v15a1 1 0 01-1 1H6a1 1 0 01-1-1V3a1 1 0 011-1z" fill="#34A853" opacity=".2"/>
                  <path d="M15 2l5 5h-5V2z" fill="#34A853"/>
                  <rect x="8" y="10" width="8" height="1.5" rx=".75" fill="#34A853"/>
                  <rect x="8" y="13" width="8" height="1.5" rx=".75" fill="#34A853"/>
                  <rect x="8" y="16" width="5" height="1.5" rx=".75" fill="#34A853"/>
                </svg>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Google Sheets — DNC Sync</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {google.connected
                    ? <span className="flex items-center gap-1 text-green-700"><CheckCircle size={11} /> {google.connected_email}</span>
                    : google.revoked
                    ? <span className="flex items-center gap-1 text-red-600"><AlertTriangle size={11} /> Token revoked — reconnect to restore sync</span>
                    : "Connect to enable automatic DNC sheet sync"}
                </div>
              </div>
            </div>

            {google.connected ? (
              <button
                onClick={handleDisconnectGoogle}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 border border-gray-200 rounded-md px-3 py-1.5 hover:border-red-200 transition-colors"
              >
                <Unlink size={12} /> Disconnect
              </button>
            ) : google.revoked ? (
              <form action="/auth/google_oauth2" method="post">
                <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
                <button type="submit" className="flex items-center gap-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md px-3 py-1.5 transition-colors">
                  <Link size={12} /> Reconnect — token was revoked
                </button>
              </form>
            ) : (
              <form action="/auth/google_oauth2" method="post">
                <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
                <button type="submit" className="flex items-center gap-1.5 text-xs font-medium text-white bg-green-700 hover:bg-green-800 rounded-md px-3 py-1.5 transition-colors">
                  <Link size={12} /> Connect Google Account
                </button>
              </form>
            )}
          </div>

          {/* Google Calendar — only shown once Google is connected */}
          {google.connected && (
            <div className="px-5 py-4 flex items-center justify-between gap-4 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 border border-gray-200">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                    <rect x="3" y="4" width="18" height="17" rx="2" fill="#4285F4" opacity=".15"/>
                    <path d="M3 9h18" stroke="#4285F4" strokeWidth="1.5"/>
                    <path d="M8 2v4M16 2v4" stroke="#4285F4" strokeWidth="1.5" strokeLinecap="round"/>
                    <rect x="7" y="13" width="4" height="3" rx=".5" fill="#4285F4" opacity=".7"/>
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Google Calendar</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {google.calendar_connected
                      ? <span className="flex items-center gap-1 text-green-700"><CheckCircle size={11} /> Connected — agent can look up upcoming meetings</span>
                      : <span className="text-amber-700">Calendar scope not granted — reconnect to enable meeting lookup</span>
                    }
                  </div>
                </div>
              </div>

              {!google.calendar_connected && (
                <form action="/auth/google_oauth2" method="post">
                  <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
                  <button type="submit" className="flex items-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md px-3 py-1.5 transition-colors">
                    <Link size={12} /> Reconnect Google
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Lateral Link CRM */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 border border-gray-200">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                    <rect x="2" y="4" width="20" height="16" rx="2" fill="#0078D4" opacity=".15"/>
                    <path d="M2 8h20M8 8v12" stroke="#0078D4" strokeWidth="1.5"/>
                    <circle cx="5" cy="6" r="1" fill="#0078D4"/>
                    <circle cx="8" cy="6" r="1" fill="#0078D4"/>
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Lateral Link CRM — DNC Layer 2</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {exchange.api_configured && exchange.connection_status === "stale"
                      ? <span className="flex items-center gap-1 text-amber-700"><CheckCircle size={11} /> Session stale — re-paste session cookie to restore</span>
                      : exchange.api_configured
                        ? <span className="flex items-center gap-1 text-green-700"><CheckCircle size={11} /> API connected — session cookie active</span>
                        : exchange.configured
                          ? <span className="text-amber-700">Credentials saved — paste session cookie below to enable API checks</span>
                          : "Enter CRM credentials to enable automated checks"}
                  </div>
                </div>
              </div>

              {exchange.configured && (
                <button
                  onClick={handleToggleAutomations}
                  className={`flex items-center gap-1.5 text-xs font-medium border rounded-md px-3 py-1.5 transition-colors ${
                    exchange.automations_paused
                      ? "text-green-700 border-green-200 hover:bg-green-50"
                      : "text-amber-700 border-amber-200 hover:bg-amber-50"
                  }`}
                >
                  {exchange.automations_paused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}
                </button>
              )}
            </div>

            <form onSubmit={submitExchange} className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-gray-500 mb-1">CRM URL <span className="text-gray-400">(configured at deploy)</span></p>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 font-mono text-gray-700 truncate">
                    {exchange.crm_url || <span className="text-gray-400">Not configured</span>}
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Username <span className="text-gray-400">(configured at deploy)</span></p>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 font-mono text-gray-700 truncate">
                    {exchange.username || <span className="text-gray-400">Not configured</span>}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Password {exchange.configured && <span className="text-gray-400 font-normal">(leave blank to keep)</span>}
                </label>
                <input
                  type="password"
                  value={exchangeForm.data.password}
                  onChange={e => exchangeForm.setData("password", e.target.value)}
                  placeholder={exchange.configured ? "••••••••" : "Enter password"}
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                />
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-600 mb-1.5">
                  Session Cookies <span className="font-normal text-gray-400">(DevTools → Application → Cookies → app.laterallink.com)</span>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      _session_id {exchange.api_configured && <span className="text-gray-400 font-normal">(leave blank to keep)</span>}
                    </label>
                    <input
                      type="password"
                      value={exchangeForm.data.session_id}
                      onChange={e => exchangeForm.setData("session_id", e.target.value)}
                      placeholder={exchange.api_configured ? "••••••••" : "Paste _session_id value"}
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      remember_consultant_token {exchange.api_configured && <span className="text-gray-400 font-normal">(leave blank to keep)</span>}
                    </label>
                    <input
                      type="password"
                      value={exchangeForm.data.remember_token}
                      onChange={e => exchangeForm.setData("remember_token", e.target.value)}
                      placeholder={exchange.api_configured ? "••••••••" : "Paste remember_consultant_token"}
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Session lasts ~13 months with "Remember me". Refresh annually.</p>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={exchangeForm.processing}
                  className="text-xs font-medium text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-md px-4 py-2 transition-colors"
                >
                  {exchange.configured ? "Update Credentials" : "Save Credentials"}
                </button>
              </div>
            </form>
          </div>

          {/* Leopard Solutions */}
          <div className="px-5 py-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 border border-gray-200">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                  <ellipse cx="12" cy="12" rx="9" ry="6" fill="#F59E0B" opacity=".2"/>
                  <circle cx="12" cy="12" r="3" fill="#F59E0B"/>
                  <circle cx="7" cy="9" r="1.5" fill="#F59E0B" opacity=".6"/>
                  <circle cx="17" cy="9" r="1.5" fill="#F59E0B" opacity=".6"/>
                  <circle cx="7" cy="15" r="1.5" fill="#F59E0B" opacity=".6"/>
                  <circle cx="17" cy="15" r="1.5" fill="#F59E0B" opacity=".6"/>
                </svg>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Leopard Solutions — Candidate Search</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {leopard.configured
                    ? <span className="flex items-center gap-1 text-green-700"><CheckCircle size={11} /> Connected as {leopard.username}</span>
                    : "Enter credentials to enable Leopard candidate searches"}
                </div>
              </div>
            </div>

            <form onSubmit={submitLeopard} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Username / Email</label>
                  <input
                    type="text"
                    value={leopardForm.data.username}
                    onChange={e => leopardForm.setData("username", e.target.value)}
                    placeholder="you@example.com"
                    className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Password {leopard.configured && <span className="text-gray-400 font-normal">(leave blank to keep)</span>}
                  </label>
                  <input
                    type="password"
                    value={leopardForm.data.password}
                    onChange={e => leopardForm.setData("password", e.target.value)}
                    placeholder={leopard.configured ? "••••••••" : "Enter password"}
                    className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={leopardForm.processing}
                  className="text-xs font-medium text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-md px-4 py-2 transition-colors"
                >
                  {leopard.configured ? "Update Credentials" : "Save Credentials"}
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* AI Assist */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="AI Assist" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-purple-50 border border-purple-100">
                <Sparkles size={16} className="text-purple-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Claude AI Assist</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {ai_assist
                    ? <span className="flex items-center gap-1 text-green-700"><CheckCircle size={11} /> Enabled — memo generation and resume optimization active</span>
                    : "Enable to auto-generate submission memos and optimize resumes via Claude"}
                </div>
              </div>
            </div>
            <button
              onClick={() => router.post("/settings/ai-assist/toggle")}
              className={`flex items-center gap-1.5 text-xs font-medium border rounded-md px-3 py-1.5 transition-colors ${
                ai_assist
                  ? "text-amber-700 border-amber-200 hover:bg-amber-50"
                  : "text-green-700 border-green-200 hover:bg-green-50"
              }`}
            >
              {ai_assist ? <><Pause size={12} /> Disable</> : <><Sparkles size={12} /> Enable</>}
            </button>
          </div>
        </section>

        {/* Go Mode — server-computed go_mode_unlocked gates visibility. When
            locked, show a small placeholder so Steven knows what's there
            and how to unlock it. */}
        {!go_mode_unlocked && (
          <section className="bg-gray-50 border border-dashed border-gray-200 rounded-lg overflow-hidden mb-6">
            <SectionHeader title="Go Mode" />
            <div className="px-5 py-4 text-xs text-gray-500 flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 border border-gray-200 flex-shrink-0">
                <Rocket size={16} className="text-gray-400" />
              </div>
              <div>
                Go Mode becomes available once AI Assist and every active automation toggle are enabled.
              </div>
            </div>
          </section>
        )}
        {go_mode_unlocked && (
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Go Mode" />
          <div className="px-5 py-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-md border flex-shrink-0 ${go_mode ? "bg-green-900 border-green-700" : "bg-gray-50 border-gray-200"}`}>
                <Rocket size={16} className={go_mode ? "text-green-300" : "text-gray-500"} />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Go Mode</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {go_mode
                    ? <span className="flex items-center gap-1 text-green-700"><CheckCircle size={11} /> Active — new candidates are auto-checked, cleared candidates get packages automatically</span>
                    : "Full automation takeover. New candidates are auto-queued for DNC checks. Cleared candidates get packages created automatically."}
                </div>
                {go_mode && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    Requires Exchange CRM to be configured and AI Assist to be enabled for full effect.
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => router.post("/settings/go-mode/toggle")}
              className={`flex items-center gap-1.5 text-xs font-medium border rounded-md px-3 py-1.5 transition-colors flex-shrink-0 ${
                go_mode
                  ? "text-amber-700 border-amber-200 hover:bg-amber-50"
                  : "text-green-700 border-green-200 hover:bg-green-50"
              }`}
            >
              {go_mode ? <><Pause size={12} /> Disable</> : <><Zap size={12} /> Enable</>}
            </button>
          </div>
        </section>
        )}
        {/* Automations */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Automation Toggles" />
          <div className="divide-y divide-gray-100">
            {([
              {
                key:      "job_alert_auto_match",
                path:     "/settings/automation/job-alert-match",
                label:    "Job Alert Auto-Matching",
                desc:     "Auto-run candidate matching when new job alerts arrive from Leopard.",
                kind:     "deterministic" as const,
                scope:    "app_flow" as const,
                triggers: "A new Leopard job alert email arrives",
                whenOff:  "Job alerts are parsed and saved, but no candidates are matched",
                value:    automations.job_alert_auto_match,
              },
              {
                key:      "submission_auto_generate",
                path:     "/settings/automation/submission-gen",
                label:    "Submission Auto-Generation",
                desc:     "Create draft submissions automatically when job alert matches are found for a candidate.",
                kind:     "deterministic" as const,
                scope:    "app_flow" as const,
                triggers: "A candidate is matched to a job alert",
                whenOff:  "Matches are recorded, but no draft submissions are created",
                value:    automations.submission_auto_generate,
              },
              {
                key:      "skm_auto_draft",
                path:     "/settings/automation/skm-auto-draft",
                label:    "SKM Auto-Drafting",
                desc:     "Auto-generate an SKM blast draft when a candidate reaches the Package Pending stage.",
                kind:     "ai_assist" as const,
                scope:    "mcp" as const,
                triggers: "Claude runs create_skm_blast at your direction",
                whenOff:  "create_skm_blast is disabled in Claude",
                value:    automations.skm_auto_draft,
              },
              {
                key:      "resume_auto_optimize",
                path:     "/settings/automation/resume-optimize",
                label:    "Resume Auto-Optimization",
                desc:     "Run the optimizer automatically when a resume is uploaded to a candidate's package.",
                kind:     "ai_assist" as const,
                scope:    "mcp" as const,
                triggers: "Claude runs optimize_resume when a resume is uploaded",
                whenOff:  "optimize_resume is disabled in Claude",
                value:    automations.resume_auto_optimize,
              },
              {
                key:      "email_sequence_auto_send",
                path:     "/settings/automation/email-sequences",
                label:    "Email Sequence Auto-Send",
                desc:     "Auto-activate follow-up email sequences after SKM blasts are sent.",
                kind:     "ai" as const,
                scope:    "mcp" as const,
                triggers: "Claude runs send_one_off_email, send_firm_email, or create_outbound_blast",
                whenOff:  "Those three tools are disabled in Claude",
                value:    automations.email_sequence_auto_send,
              },
            ] as { key: string; path: string; label: string; desc: string; kind: "deterministic" | "ai" | "ai_assist"; scope: "app_flow" | "mcp" | "both"; triggers: string; whenOff: string; value: boolean }[]).map((toggle) => (
              <div key={toggle.key} className="flex items-start justify-between px-5 py-4 gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="text-sm font-medium text-gray-800">{toggle.label}</p>
                    <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                      toggle.kind === "deterministic"
                        ? "bg-gray-50 border-gray-200 text-gray-500"
                        : toggle.kind === "ai_assist"
                          ? "bg-purple-50 border-purple-200 text-purple-700"
                          : "bg-purple-50 border-purple-200 text-purple-600"
                    }`}>
                      {toggle.kind === "deterministic" ? "Deterministic" : toggle.kind === "ai_assist" ? "AI · Needs AI Assist" : "AI"}
                    </span>
                    <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                      toggle.scope === "app_flow"
                        ? "bg-blue-50 border-blue-200 text-blue-600"
                        : toggle.scope === "mcp"
                          ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                          : "bg-violet-50 border-violet-200 text-violet-600"
                    }`}>
                      {toggle.scope === "app_flow" ? "App flow" : toggle.scope === "mcp" ? "MCP" : "App flow + MCP"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{toggle.desc}</p>
                  <p className="text-xs text-gray-300 mt-1.5"><span className="text-gray-400 font-medium">Triggers:</span> {toggle.triggers}</p>
                  <p className="text-xs text-gray-300 mt-0.5"><span className="text-gray-400 font-medium">When off:</span> {toggle.whenOff}</p>
                </div>
                <button
                  onClick={() => router.post(toggle.path)}
                  className={`shrink-0 text-xs px-3 py-1.5 rounded border font-medium transition-colors mt-0.5 ${
                    toggle.value
                      ? "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                      : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {toggle.value ? "On" : "Off"}
                </button>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 bg-amber-50 border-t border-amber-100">
            <p className="text-xs text-amber-700">All automations are off by default. Enable only what you trust.</p>
          </div>
        </section>

        {/* Practice Area Blocklist */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Practice Area Blocklist" />
          <div className="px-5 py-4">
            <p className="text-xs text-gray-500 mb-3">
              Practice areas listed here are skipped at the deterministic gate before any AI evaluation cost.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]">
              {blocklistChips.map((chip) => (
                <span key={chip} className="inline-flex items-center gap-1 text-xs bg-red-50 border border-red-200 text-red-700 rounded px-2 py-0.5">
                  {chip}
                  <button
                    type="button"
                    onClick={() => removeBlocklistChip(chip)}
                    className="text-red-400 hover:text-red-700 transition-colors leading-none"
                    aria-label={`Remove ${chip}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {blocklistChips.length === 0 && (
                <span className="text-xs text-gray-300 italic">No practice areas blocked</span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={blocklistInput}
                onChange={(e) => setBlocklistInput(e.target.value)}
                onKeyDown={handleBlocklistKeyDown}
                onBlur={() => addBlocklistChip(blocklistInput)}
                placeholder="e.g. Patent Prosecution — press Enter to add"
                className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-400"
              />
              <button
                type="button"
                onClick={saveBlocklist}
                disabled={blocklistSaving}
                className="text-xs font-medium text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-50 rounded-md px-4 py-2 transition-colors shrink-0"
              >
                Save
              </button>
            </div>
          </div>
        </section>

        {/* MCP Connection */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="MCP Connection — Claude AI" />
          {mcp.configured ? (
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-gray-500">
                Connect Steven's Claude account to Docket. In Claude AI → Settings → Connectors → Add custom connector:
              </p>
              <CopyField label="MCP Server URL" value={mcp.mcp_url ?? ""} />
              <CopyField label="Client ID" value={mcp.client_id ?? ""} />
              <CopyField label="Client Secret" value={mcp.client_secret ?? ""} />
              <p className="text-xs text-gray-400">
                First use: Claude will open Docket's login page. Sign in once — tokens refresh automatically.
              </p>
            </div>
          ) : (
            <div className="px-5 py-4 text-sm text-amber-700 bg-amber-50">
              MCP OAuth app not seeded. Run <code className="text-xs bg-amber-100 px-1 py-0.5 rounded">rails db:seed</code> to generate credentials.
            </div>
          )}
        </section>

        {/* Email Templates */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Email Templates" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Template Management</div>
              <div className="text-xs text-gray-500 mt-0.5">Edit subject and body templates for all outreach types.</div>
            </div>
            <InertiaLink
              href="/settings/email-templates"
              className="flex items-center gap-1.5 text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded-md px-3 py-1.5 transition-colors shrink-0"
            >
              Manage Templates
            </InertiaLink>
          </div>
        </section>

        {/* Voice Examples */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Voice Examples" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">AI Writing Samples</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Manage the cover letter, SKM, memo, and resume examples the AI drafts from. Add your own — real names are scrubbed automatically.
              </div>
            </div>
            <InertiaLink
              href="/settings/voice-examples"
              className="flex items-center gap-1.5 text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded-md px-3 py-1.5 transition-colors shrink-0"
            >
              Manage Examples
            </InertiaLink>
          </div>
        </section>

        {/* Canonical Cities */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="DNC Cities" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Canonical City List</div>
              <div className="text-xs text-gray-500 mt-0.5">Add cities for DNC office scope matching without a deploy.</div>
            </div>
            <InertiaLink
              href="/settings/canonical-cities"
              className="flex items-center gap-1.5 text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded-md px-3 py-1.5 transition-colors shrink-0"
            >
              Manage Cities
            </InertiaLink>
          </div>
        </section>

        {/* AI Usage */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="AI Usage" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Evaluation Spend</div>
              <div className="text-xs text-gray-500 mt-0.5">Month-to-date token usage and cost per job evaluation.</div>
            </div>
            <InertiaLink
              href="/settings/usage"
              className="flex items-center gap-1.5 text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded-md px-3 py-1.5 transition-colors shrink-0"
            >
              View Usage
            </InertiaLink>
          </div>
        </section>

        {/* MCP Tools Reference */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="MCP Tools Reference" />
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Claude AI Tools</div>
              <div className="text-xs text-gray-500 mt-0.5">Browse all available MCP tools, their parameters, and what they do.</div>
            </div>
            <InertiaLink
              href="/settings/mcp-tools"
              className="flex items-center gap-1.5 text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded-md px-3 py-1.5 transition-colors shrink-0"
            >
              View Tools
            </InertiaLink>
          </div>
        </section>

        {/* Email Campaigns */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Email Campaigns" />
          <div className="px-5 py-4 space-y-5">
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">No-response window</p>
              <p className="text-xs text-gray-500 mb-3">
                Skip candidates who were already sent an email within this many days via any campaign.
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={dedupWindow}
                  onChange={(e) => setDedupWindow(Math.max(1, Math.min(365, Number(e.target.value))))}
                  className="w-24 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                />
                <span className="text-xs text-gray-500">days</span>
                <button
                  onClick={saveDedupWindow}
                  disabled={dedupWindowSaving}
                  className="text-xs font-medium text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-md px-4 py-2 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-5">
              <p className="text-xs font-medium text-gray-700 mb-1">Placed candidate window</p>
              <p className="text-xs text-gray-500 mb-3">
                Skip candidates who were placed within this many days. Applies regardless of whether they've been emailed.
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={1825}
                  value={placedDedupWindow}
                  onChange={(e) => setPlacedDedupWindow(Math.max(1, Math.min(1825, Number(e.target.value))))}
                  className="w-24 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-600"
                />
                <span className="text-xs text-gray-500">days</span>
                <button
                  onClick={savePlacedDedupWindow}
                  disabled={placedDedupSaving}
                  className="text-xs font-medium text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-md px-4 py-2 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Billing */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <SectionHeader title="Billing" />
          <BillingSection billing={billing} />
        </section>
      </div>
    </AppLayout>
  );
}
